import { OnBoarderWorkflowService } from '@/modules/onboarder/services/onboarder-workflow.service';
import { object, string, array, number, boolean, date } from 'yup';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';
const onboarderService = new OnBoarderWorkflowService();
const optionalEmail = () => string()
  .transform(value => value === '' ? null : value)
  .email()
  .optional()
  .nullable();

const buildDynamicHolderSchema = (holder, app, requirements, formData) => {
  const personClass = holder.personClass;
  const accountClass = app.accountClass;

  const isRequired = (infoSet) => {
    const match = requirements.find(r =>
      r.informationSet === infoSet &&
      r.personClass && r.personClass.some(p => p.personClass === personClass) &&
      r.accountClass && r.accountClass.some(a => a.class === accountClass || ((a.class === 'Member' || a.class === 'Regular') && (accountClass === 'Member' || accountClass === 'Regular')))
    );
    return match ? match.required === true : false;
  };

  const schemaShape = {
    personClass: string().optional().nullable(),
    title: string().optional().nullable(),
    firstName: string().min(2).max(50).optional().nullable(),
    lastName: string().min(2).max(50).optional().nullable(),
    middleName: string().optional().nullable(),
    alias: string()
      .transform((value) => (value === '' ? null : value))
      .min(2)
      .max(20)
      .optional()
      .nullable(),
    gender: string().optional().nullable(),
    dob: date().optional().nullable(),
    emailAddress: optionalEmail(),
    trn: string().optional().nullable(),
    maritalStatus: string().optional().nullable(),
    riskScore: number().min(0).required(),
    riskRating: string().optional().nullable(),
    specialConnections: object({
      isPEP: boolean(),
      isRelatedToPEP: boolean(),
      isUSCitizen: boolean(),
      isRelatedToEmployee: boolean()
    }),
    riskFactors: object({
      profession: number().required().min(0),
      industry: number().required().min(0),
      connections: number().required().min(0),
      country: number().required().min(0),
      depositRange: number().required().min(0),
      accountBalance: number().required().min(0),
      methodOfOpeningAccount: number().required().min(0)
    }),
    watchListMatches: array().min(0).optional().nullable()
  };

  schemaShape.documents = array()
    .min(1, 'document')
    .of(
      object({
        verificationStatus: string().optional().nullable(),
        document: string().required(),
        comment: string().optional().nullable()
      })
    );

  const hasCell = formData?.personalInformation?.some(field => field.field === 'Tele No (cell)' && field.required);
  const hasHome = formData?.personalInformation?.some(field => field.field === 'Tele No (home)' && field.required);

  if (isRequired('Contact Information') && (!formData || hasCell || hasHome)) {
    schemaShape.teleNo = array()
      .min(1, 'contact')
      .of(
        object({
          type: string().required(),
          number: string().required()
        })
      ).required();
  } else {
    schemaShape.teleNo = array().optional().nullable();
  }

  const hasHomeAddress = formData?.homeAddress?.some(field => field.required);
  const hasMailingAddress = formData?.mailingAddress?.some(field => field.required);

  if (
    (isRequired('Home Address') && (!formData || hasHomeAddress)) ||
    (isRequired('Mailing Address') && (!formData || hasMailingAddress))
  ) {
    schemaShape.address = array()
      .min(1, 'address')
      .of(
        object({
          type: string().optional().nullable(),
          province: string().optional().nullable(),
          city: string().optional().nullable(),
          street: string().optional().nullable(),
          zipCode: string().optional().nullable(),
          countryOfResidence: string().optional().nullable(),
          nationality: string().optional().nullable()
        })
      ).required();
  } else {
    schemaShape.address = array().optional().nullable();
  }

  const hasRequiredEmploymentField = formData?.employmentInformation?.some(
    field => field.required
  );

  if (
    isRequired('Employment Information') &&
    (!formData || hasRequiredEmploymentField)
  ) {
    schemaShape.employmentDetails = array()
      .min(1, 'employment')
      .of(
        object({
          employmentStatus: string().optional().nullable(),
          nameOfEmployer: string().optional().nullable(),
          country: string().optional().nullable(),
          contactNumber: string().optional().nullable(),
          state: string().optional().nullable(),
          city: string().optional().nullable(),
          street: string().optional().nullable(),
          email: optionalEmail(),
          profession: string().optional().nullable(),
          businessIndustry: string().optional().nullable(),
          monthlyIncome: number().min(0).optional().nullable()
        })
      ).required();
  } else {
    schemaShape.employmentDetails = array().optional().nullable();
  }

  if (isRequired('Identification')) {
    schemaShape.idInformation = array()
      .min(1, 'idInfo')
      .of(
        object({
          number: string().optional().nullable(),
          type: string().optional().nullable(),
          issueDate: date().optional().nullable(),
          expirationDate: date().optional().nullable(),
          issuingCountry: string().optional().nullable(),
          verificationStatus: string().required()
        })
      ).required();
  } else {
    schemaShape.idInformation = array().optional().nullable();
  }

  if (isRequired('References')) {
    schemaShape.references = array()
      .min(1, 'references')
      .of(
        object({
          firstName: string().optional().nullable(),
          lastName: string().optional().nullable(),
          country: string().optional().nullable(),
          province: string().optional().nullable(),
          city: string().optional().nullable(),
          street: string().optional().nullable(),
          emailAddress: optionalEmail(),
          teleNo: string().optional().nullable(),
          position: string().optional().nullable(),
          refereeResponse: string().optional().nullable(),
          comment: string().optional().nullable(),
          verificationStatus: string().required()
        })
      ).required();
  } else {
    schemaShape.references = array().optional().nullable();
  }

  if (isRequired('Connected Persons')) {
    schemaShape.connectedPersons = array()
      .min(1, 'connected')
      .of(
        object({
          firstName: string().optional().nullable(),
          lastName: string().optional().nullable(),
          dob: date().optional().nullable(),
          country: string().optional().nullable(),
          state: string().optional().nullable(),
          city: string().optional().nullable(),
          street: string().optional().nullable(),
          email: optionalEmail(),
          relation: string().optional().nullable(),
          contactNumber: string().optional().nullable()
        })
      ).required();
  } else {
    schemaShape.connectedPersons = array().optional().nullable();
  }

  if (isRequired('Source of Funds')) {
    schemaShape.sourceOfFunds = array()
      .min(1, 'funds')
      .of(
        object({
          type: string().required(),
          comment: string().optional().nullable(),
          verificationStatus: string().required()
        })
      ).required();
  } else {
    schemaShape.sourceOfFunds = array().optional().nullable();
  }

  if (isRequired('Source of Wealth')) {
    schemaShape.sourceOfWealth = array()
      .min(1, 'wealth')
      .of(
        object({
          type: string().required(),
          comment: string().optional().nullable(),
          verificationStatus: string().required()
        })
      ).required();
  } else {
    schemaShape.sourceOfWealth = array().optional().nullable();
  }

  return object().shape(schemaShape);
};

const buildDynamicVerificationSchema = (holder, app, requirements) => {
  const personClass = holder.personClass;
  const accountClass = app.accountClass;

  const isRequired = (infoSet) => {
    const match = requirements.find(r =>
      r.informationSet === infoSet &&
      r.personClass && r.personClass.some(p => p.personClass === personClass) &&
      r.accountClass && r.accountClass.some(a => a.class === accountClass || ((a.class === 'Member' || a.class === 'Regular') && (accountClass === 'Member' || accountClass === 'Regular')))
    );
    return match ? match.required === true : false;
  };

  const schemaShape = {};

  schemaShape.documents = array()
    .min(1, 'document')
    .of(
      object({
        verificationStatus: string()
          .required()
          .oneOf(['Verified', 'Waived'])
      })
    );

  if (isRequired('Home Address') || isRequired('Mailing Address')) {
    schemaShape.address = array()
      .min(1, 'address')
      .of(
        object({
          verificationStatus: string()
            .required()
            .oneOf(['Verified', 'Waived'])
        })
      );
  } else {
    schemaShape.address = array().optional().nullable();
  }

  if (isRequired('Identification')) {
    schemaShape.idInformation = array()
      .min(1, 'idInfo')
      .of(
        object({
          verificationStatus: string()
            .required()
            .oneOf(['Verified', 'Waived'])
        })
      );
  } else {
    schemaShape.idInformation = array().optional().nullable();
  }

  if (isRequired('References')) {
    schemaShape.references = array()
      .min(1, 'references')
      .of(
        object({
          verificationStatus: string()
            .required()
            .oneOf(['Verified', 'Waived'])
        })
      );
  } else {
    schemaShape.references = array().optional().nullable();
  }

  if (isRequired('Source of Funds')) {
    schemaShape.sourceOfFunds = array()
      .min(1, 'funds')
      .of(
        object({
          verificationStatus: string()
            .required()
            .oneOf(['Verified', 'Waived'])
        })
      );
  } else {
    schemaShape.sourceOfFunds = array().optional().nullable();
  }

  if (isRequired('Source of Wealth')) {
    schemaShape.sourceOfWealth = array()
      .min(1, 'wealth')
      .of(
        object({
          verificationStatus: string()
            .required()
            .oneOf(['Verified', 'Waived'])
        })
      );
  } else {
    schemaShape.sourceOfWealth = array().optional().nullable();
  }

  return object().shape(schemaShape);
};

export const state = {
  allOnboarderApplications: [],
  allAppsByStatus: [],
  currentApplication: null,
  currentAccountHolder: null,
  currentTelNo: null,
  currentIdInformation: null,
  currentAddress: null,
  currentReference: null,
  currentDocument: null,
  currentConnectedPerson: null,
  currentEmploymentDetail: null,
  currentSourceOfFund: null,
  currentSourceOfWealth: null,
  currentBeneficiary: null,
  currentProduct: null
};
export const mutations = {
  ...make.mutations(state),
  UPDATE_CURRENT_ACCOUNT_HOLDER(state) {
    state.currentAccountHolder = state.currentApplication.accountHolder.find(
      (a) => a.id == state.currentAccountHolder.id
    );
  },
  UPDATE_CURRENT_ACCOUNT_HOLDER_VALUES(state, payload) {
    state.currentAccountHolder = { ...state.currentAccountHolder, ...payload };
  }
};

export const getters = {
  ...make.getters(state),
  beneficiary: (state) => {
    return state.currentApplication ? state.currentApplication.beneficiary : [];
  },
  productsOrServices: (state) => {
    return state.currentApplication
      ? state.currentApplication.productsOrServices
      : [];
  },
  // Vuex passes - state, getters, rootState, rootGetters
  /**
   * This getter will tell what workflow access the logged in user has
   */
  workflowAccess: (_, __, ___, rootGetters) => {
    const roleAccess = rootGetters['authModule/roleAccess'];
    const processApplication = roleAccess('Process Application');
    const watchlistSearch = roleAccess('Perform Watchlist Search');
    const verifyDocuments = roleAccess('Verify Documents');
    const conductReview = roleAccess('Conduct Review');
    const approveApplication = roleAccess('Approve Application');
    const createAccount = roleAccess('Create Account');
    return {
      processApplication,
      watchlistSearch,
      verifyDocuments,
      conductReview,
      approveApplication,
      createAccount
    };
  }
};

export const actions = {
  // Overall application actions
  CreateApplication: async ({ rootState }, payload) => {
    try {
      const workflowConfigSnapshot = {
        workflowStages: rootState.workflowStagesModule.workflowStages,
        applicationRequirements: rootState.applicationDataRequirementsModule.applicationDataRequirements,
        formData: rootState.formDataSettingsModule.formData,
        services: rootState.serviceSettingsModule.services,
        products: rootState.accountProductSettingsModule.accountProductSettings,
        relationTypes: rootState.relationSettingsModule.relationSettings,
        referenceTypes: rootState.referenceTypesModule.referenceTypes,
        idTypes: rootState.idTypesModule.idTypes,
        sourceOfWealth: rootState.sourceOfWealthModule.sourceOfWealthSettings,
        sourceOfIncomeTypes: rootState.sourceOfIncomeTypesModule.sourceOfIncomeTypes,
        accountTypes: rootState.accountClassificationModule.accountClassifications,
        accountClasses: rootState.accountClassificationModule.accountClasses,
        personClasses: rootState.personClassModule.personClasses,
        dueDiligenceGuides: rootState.dueDiligenceModule.dueDiligenceGuides,
        dueDiligenceTypes: rootState.dueDiligenceModule.dueDiligenceTypes,
        dueDiligenceActions: rootState.dueDiligenceActionsModule.dueDiligenceActions,
        onboardingDocuments: rootState.onboarderDocumentSettingsModule.onboarderDocuments,
        riskFactors: rootState.riskFactorModule.riskFactorByModuleName
      };
      payload.workflowConfigSnapshot = workflowConfigSnapshot;
      const { data } = await onboarderService.CreateApplication(payload);
      return data;
    } catch (error) {
      Logger.error('creating onboarder application', error);
      throw error;
    }
  },
  UpdateApplication: async (_, { id, payload }) => {
    try {
      const { data } = await onboarderService.UpdateApplication(id, payload);
      return data;
    } catch (error) {
      Logger.error('updating source of wealth settings');
      throw error;
    }
  },
  GetAllApplications: async ({ commit }) => {
    try {
      const { data } = await onboarderService.GetAllApplications();
      commit('SET_ALL_ONBOARDER_APPLICATIONS', data);
      return data;
    } catch (error) {
      Logger.error('getting all onboarder applications', error);
      throw error;
    }
  },
  GetOneApplication: async ({ commit }, id) => {
    try {
      const { data } = await onboarderService.GetOneApplication(id);
      commit('SET_CURRENT_APPLICATION', data);
      return data;
    } catch (error) {
      Logger.error('getting one onboarder application', error);
      throw error;
    }
  },
  DeleteApplication: async (_, id) => {
    try {
      const { data } = await onboarderService.DeleteApplication(id);
      return data;
    } catch (error) {
      Logger.error('deleting one onboarder application', error);
      throw error;
    }
  },
  UpdateAppVerificationStatus: async (_, { id, data }) => {
    try {
      const info = await onboarderService.UpdateAppVerificationStatus(id, data);
      return info.data;
    } catch (error) {
      Logger.error("updating application's verification status", error);
      throw error;
    }
  },
  GetAppsByStatus: async ({ commit }, status) => {
    try {
      const { data } = await onboarderService.GetAppsByStatus(status);
      commit('SET_ALL_APPS_BY_STATUS', data);
      return data;
    } catch (error) {
      Logger.error('getting all onboarder applications by status', error);
      throw error;
    }
  },
  CreateAccount: async (_, info) => {
    try {
      const { data } = await onboarderService.CreateAccount(info);
      return data;
    } catch (error) {
      Logger.error('creating account by number', error);
      throw error;
    }
  },

  // Printable document request
  HtmlTemplate: async (_, { id, options }) => {
    try {
      const { data } = await onboarderService.HtmlTemplate(id, options);
      return data;
    } catch (error) {
      Logger.error('printing document', error);
      throw error;
    }
  },

  // Validation for entire application
  ValidateAppStart: async ({ state }) => {
    // check if the app is created at all
    if (!state.currentApplication) return false;
    return true;
  },

  // Account holder actions
  CreateAccountHolder: async (_, { appId, payload }) => {
    try {
      const { data } = await onboarderService.CreateAccountHolder(
        appId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating account holder', error);
      throw error;
    }
  },
  UpdateAccountHolder: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateAccountHolder(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating account holder', error);
      throw error;
    }
  },
  DeleteNearestContact: async (_, { appId, accountId }) => {
    try {
      const { data } = await onboarderService.DeleteNearestContact(
        appId,
        accountId
      );
      return data;
    } catch (error) {
      Logger.error('deleting nearest contact', error);
      throw error;
    }
  },
  DeleteAccountHolder: async (_, { appId, accountId }) => {
    try {
      const { data } = await onboarderService.DeleteAccountHolder(
        appId,
        accountId
      );
      return data;
    } catch (error) {
      Logger.error('deleting account holder', error);
      throw error;
    }
  },
  // Validate each account holder on the account
  ValidateAppAccountHolders: async ({ state, rootState }) => {
    // check if account exists
    if (!state.currentApplication) return false;
    if (!state.currentApplication.accountHolder) return false;
    if (state.currentApplication.accountHolder.length == 0) return false;

    const app = state.currentApplication;
    const requirements = app.workflowConfigSnapshot?.applicationRequirements || rootState.applicationDataRequirementsModule.applicationDataRequirements || [];
    const formData = app.workflowConfigSnapshot?.formData || rootState.formDataSettingsModule.formData;

    try {
      // loop over account holders and validate each
      for (let i = 0; i < state.currentApplication.accountHolder.length; i++) {
        const holder = state.currentApplication.accountHolder[i];
        const schema = buildDynamicHolderSchema(holder, app, requirements, formData);
        await schema.validate(holder);
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  },

  // Validate each verification item
  ValidateVerificationDocuments: async ({ state, rootState }) => {
    // check if account exists
    if (!state.currentApplication) return false;
    if (!state.currentApplication.accountHolder) return false;
    if (state.currentApplication.accountHolder.length == 0) return false;

    const app = state.currentApplication;
    const requirements = app.workflowConfigSnapshot?.applicationRequirements || rootState.applicationDataRequirementsModule.applicationDataRequirements || [];

    try {
      // loop over account holders and validate each
      for (let i = 0; i < state.currentApplication.accountHolder.length; i++) {
        const holder = state.currentApplication.accountHolder[i];
        const schema = buildDynamicVerificationSchema(holder, app, requirements);
        await schema.validate(holder);
      }
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  },

  // Tel No actions
  CreateTelNo: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateTelNo(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating telephone number', error);
      throw error;
    }
  },
  UpdateTelNo: async (_, { appId, accountId, telId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateTelNo(
        appId,
        accountId,
        telId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating telephone number', error);
      throw error;
    }
  },
  DeleteTelNo: async (_, { appId, accountId, telId }) => {
    try {
      const { data } = await onboarderService.DeleteTelNo(
        appId,
        accountId,
        telId
      );
      return data;
    } catch (error) {
      Logger.error('deleting telephone number', error);
      throw error;
    }
  },

  // id Information actions
  CreateIdInformation: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateIdInformation(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating id information', error);
      throw error;
    }
  },
  UpdateIdInformation: async (_, { appId, accountId, idId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateIdInformation(
        appId,
        accountId,
        idId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating id information', error);
      throw error;
    }
  },
  DeleteIdInformation: async (_, { appId, accountId, idId }) => {
    try {
      const { data } = await onboarderService.DeleteIdInformation(
        appId,
        accountId,
        idId
      );
      return data;
    } catch (error) {
      Logger.error('deleting id information', error);
      throw error;
    }
  },

  // Address Information actions
  CreateAddressInformation: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateAddressInformation(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating address information', error);
      throw error;
    }
  },
  UpdateAddressInformation: async (
    _,
    { appId, accountId, addressId, payload }
  ) => {
    try {
      const { data } = await onboarderService.UpdateAddressInformation(
        appId,
        accountId,
        addressId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating address information', error);
      throw error;
    }
  },
  DeleteAddressInformation: async (_, { appId, accountId, addressId }) => {
    try {
      const { data } = await onboarderService.DeleteAddressInformation(
        appId,
        accountId,
        addressId
      );
      return data;
    } catch (error) {
      Logger.error('deleting address information', error);
      throw error;
    }
  },

  // Reference Information actions
  CreateReference: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateReference(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating reference information', error);
      throw error;
    }
  },
  UpdateReference: async (_, { appId, accountId, referenceId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateReference(
        appId,
        accountId,
        referenceId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating reference information', error);
      throw error;
    }
  },
  DeleteReference: async (_, { appId, accountId, referenceId }) => {
    try {
      const { data } = await onboarderService.DeleteReference(
        appId,
        accountId,
        referenceId
      );
      return data;
    } catch (error) {
      Logger.error('deleting reference information', error);
      throw error;
    }
  },

  // Document actions
  CreateDocument: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateDocument(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating document information', error);
      throw error;
    }
  },
  UpdateDocument: async (_, { appId, accountId, documentId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateDocument(
        appId,
        accountId,
        documentId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating document information', error);
      throw error;
    }
  },
  DeleteDocument: async (_, { appId, accountId, documentId }) => {
    try {
      const { data } = await onboarderService.DeleteDocument(
        appId,
        accountId,
        documentId
      );
      return data;
    } catch (error) {
      Logger.error('deleting document information', error);
      throw error;
    }
  },

  // Connected Person actions
  CreateConnectedPerson: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateConnectedPerson(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating connected person', error);
      throw error;
    }
  },
  UpdateConnectedPerson: async (_, { appId, accountId, personId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateConnectedPerson(
        appId,
        accountId,
        personId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating connected person', error);
      throw error;
    }
  },
  DeleteConnectedPerson: async (_, { appId, accountId, personId }) => {
    try {
      const { data } = await onboarderService.DeleteConnectedPerson(
        appId,
        accountId,
        personId
      );
      return data;
    } catch (error) {
      Logger.error('deleting connected person', error);
      throw error;
    }
  },

  // Employment actions
  CreateEmploymentInformation: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateEmploymentInformation(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating employment information', error);
      throw error;
    }
  },
  UpdateEmploymentInformation: async (
    _,
    { appId, accountId, employmentId, payload }
  ) => {
    try {
      const { data } = await onboarderService.UpdateEmploymentInformation(
        appId,
        accountId,
        employmentId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating employment information', error);
      throw error;
    }
  },
  DeleteEmploymentInformation: async (
    _,
    { appId, accountId, employmentId }
  ) => {
    try {
      const { data } = await onboarderService.DeleteEmploymentInformation(
        appId,
        accountId,
        employmentId
      );
      return data;
    } catch (error) {
      Logger.error('deleting employment information', error);
      throw error;
    }
  },

  // Source of funds actions
  CreateSourceOfFunds: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.CreateSourceOfFunds(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating source of funds', error);
      throw error;
    }
  },
  UpdateSourceOfFunds: async (_, { appId, accountId, sourceId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateSourceOfFunds(
        appId,
        accountId,
        sourceId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating source of funds', error);
      throw error;
    }
  },
  DeleteSourceOfFunds: async (_, { appId, accountId, sourceId }) => {
    try {
      const { data } = await onboarderService.DeleteSourceOfFunds(
        appId,
        accountId,
        sourceId
      );
      return data;
    } catch (error) {
      Logger.error('deleting source of funds', error);
      throw error;
    }
  },

  // Source of funds actions
  createSourceOfWealth: async (_, { appId, accountId, payload }) => {
    try {
      const { data } = await onboarderService.createSourceOfWealth(
        appId,
        accountId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating source of wealth', error);
      throw error;
    }
  },
  updateSourceOfWealth: async (_, { appId, accountId, sourceId, payload }) => {
    try {
      const { data } = await onboarderService.updateSourceOfWealth(
        appId,
        accountId,
        sourceId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating source of wealth', error);
      throw error;
    }
  },
  DeleteSourceOfWealth: async (_, { appId, accountId, sourceId }) => {
    try {
      const { data } = await onboarderService.DeleteSourceOfWealth(
        appId,
        accountId,
        sourceId
      );
      return data;
    } catch (error) {
      Logger.error('deleting source of wealth', error);
      throw error;
    }
  },

  // Beneficiary actions
  CreateBeneficiary: async (_, { appId, payload }) => {
    try {
      const { data } = await onboarderService.CreateBeneficiary(appId, payload);
      return data;
    } catch (error) {
      Logger.error('creating beneficiary information', error);
      throw error;
    }
  },
  UpdateBeneficiary: async (_, { appId, beneficiaryId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateBeneficiary(
        appId,
        beneficiaryId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating beneficiary information', error);
      throw error;
    }
  },
  DeleteBeneficiary: async (_, { appId, beneficiaryId }) => {
    try {
      const { data } = await onboarderService.DeleteBeneficiary(
        appId,
        beneficiaryId
      );
      return data;
    } catch (error) {
      Logger.error('deleting beneficiary information', error);
      throw error;
    }
  },

  // Product/Service actions
  CreateProductOrService: async (_, { appId, payload }) => {
    try {
      const { data } = await onboarderService.CreateProductOrService(
        appId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('creating product/service', error);
      throw error;
    }
  },
  UpdateProductOrService: async (_, { appId, productId, payload }) => {
    try {
      const { data } = await onboarderService.UpdateProductOrService(
        appId,
        productId,
        payload
      );
      return data;
    } catch (error) {
      Logger.error('updating product/service', error);
      throw error;
    }
  },
  DeleteProductOrService: async (_, { appId, productId }) => {
    try {
      const { data } = await onboarderService.DeleteProductOrService(
        appId,
        productId
      );
      return data;
    } catch (error) {
      Logger.error('deleting product/service', error);
      throw error;
    }
  },

  //Application Pool Actions
  AssignApplicationToUser: async (_, { applicationId, assigneeName }) => {
    try {
      const { data } = await onboarderService.AssignApplicationToUser(
        applicationId,
        assigneeName
      );
      return data;
    } catch (error) {
      Logger.error('assigning application to user', error);
      throw error;
    }
  },

  DeassignApplicationToUser: async (_, { applicationId }) => {
    try {
      const { data } = await onboarderService.DeassignApplicationFromUser(
        applicationId
      );
      return data;
    } catch (error) {
      Logger.error('deassigning application to user', error);
      throw error;
    }
  }
};
